import includes from 'lodash/includes';
import { useHistory, generatePath } from 'react-router-dom';
import { useSelector, shallowEqual, useDispatch } from 'react-redux';

import { useStateMachineContext } from 'machines/hooks/useStateMachineContext';
import { useNavigateToExternalURL } from 'hooks/useNavigateToExternalURL';
import { routePath } from 'config/route-config';
import {
  selectHistory,
  popHistory,
  pushHistory,
  replaceHistory
} from 'state/journey-routes/journey-routes-slice';
import { addQueryString, addHash } from 'machines/utils/misc';
import { store } from 'state/store';
import { ReactRenderingError } from 'errors/ReactRenderingError';
import { checkMatchPath } from 'utils/misc';
import { usePartner } from 'components/app/routes/usePartner';

export const useHistoryNavigation = () => {
  const history = useHistory();
  const navigateToExternalURL = useNavigateToExternalURL();
  const dispatch = useDispatch();
  const historyStack = useSelector(selectHistory, shallowEqual);
  const { actor } = useStateMachineContext();
  const { partnerPath } = usePartner();

  return (params) => {
    const {
      path,
      routeParams,
      queryParams,
      hash,
      wasVisited,
      replace,
      callback
    } = params;
    const isInternalPath = includes(routePath, path);
    const state = {
      store: store.getState(),
      snapshot: actor?.getSnapshot().toJSON(),
      partnerName: partnerPath
    };

    if (isInternalPath) {
      const pathWithParams = generatePath(path, routeParams);
      const pathWithQueryParams = addQueryString(pathWithParams, queryParams);
      const fullPath = addHash(pathWithQueryParams, hash);

      if (wasVisited) {
        const reversePreviousPathIndex = [...historyStack]
          .reverse()
          .findIndex((path) => {
            const pathWithoutQueryParams = path.split('?')[0];
            return checkMatchPath(pathWithoutQueryParams, pathWithParams);
          });
        history.go(-1 * reversePreviousPathIndex);
        dispatch(popHistory(pathWithParams));
      } else if (replace) {
        history.replace(fullPath, state);
        dispatch(replaceHistory(fullPath));
      } else {
        const currentFullPath = addHash(
          addQueryString(history.location.pathname, history.location.search),
          history.location.hash
        );
        history.replace(currentFullPath, state); // save state to history for current path before navigating to next path
        history.push(fullPath);
        dispatch(pushHistory(fullPath));
      }
    } else {
      history.replace(history.location.pathname, state); // save state to history for current path before navigating to next path
      const nextUrl = addQueryString(path, queryParams);
      navigateToExternalURL(nextUrl);
    }
    // callback to be executed after navigation and use setTimeput to excute
    // after the popstate event
    callback && setTimeout(callback, 0);
  };
};

export const useNavigation = () => {
  const { actor, eventTypes } = useStateMachineContext();
  const navigate = useHistoryNavigation();

  const next = (params) => {
    const { eventType } = params;
    const isValidEvent = Object.values(eventTypes).includes(eventType);
    if (!isValidEvent) {
      throw new ReactRenderingError({
        error: new Error(
          `Invalid event type '${eventType}' send to state machine.\nValid eventTypes:\n 
          ${Object.values(eventTypes).join('\n')} `
        )
      });
    }

    const payload = {
      callback: ({ path }) => {
        navigate({
          ...params,
          path
        });
      },
      actor
    };

    actor?.send({ type: eventType, payload });
  };

  return { eventTypes, next };
};
